import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { Box, Button, IconButton, Stack, Tab, Tabs, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import useDrawerContext from 'v2/components/Drawer/useDrawerContext';
import Loader from 'v2/components/Loader/Loader';
import useModalContext from 'v2/components/Modal/useModalContext';
import SetupIndicator from 'v2/components/SetupIndicator/SetupIndicator';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import useDeviceGroupDetails from 'v2/hooks/useDeviceGroupDetails/useDeviceGroupDetails';
import useRequiredParam from 'v2/hooks/useRequiredParam/useRequiredParam';
import NotFound from 'v2/pages/GeneralErrors/NotFound';
import SomethingWentWrong from 'v2/pages/GeneralErrors/SomethingWentWrong';
import { DEVICE_GROUPS_PATH } from 'v2/paths';
import DeleteDeviceGroupModal from './components/DeleteDeviceGroupModal';
import DevicesTab from './components/DevicesTab';
import UserGroupsTab from './components/UserGroupsTab';
import EditDeviceGroupDrawer from './drawers/EditDeviceGroupDrawer';
import EditDevicesDrawer from './drawers/EditDevicesDrawer';

const DeviceGroupDetails: React.FC = () => {
  const { translation } = useTranslation();
  const groupId = useRequiredParam('groupId');
  const { showSnackbar } = useSnackbarContext();
  const { openModal, closeModal } = useModalContext();
  const { openDrawer } = useDrawerContext();
  const navigate = useNavigate();
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const {
    deviceGroup,
    doorsInGroup,
    allDoors,
    userGroupsForGroup,
    loading,
    hasDeviceGroupFetchError,
    retryFetchingDeviceGroup,
    deleteDeviceGroup,
    toggleUserGroupAccess,
    updateDeviceGroupWithDoors,
    renameDeviceGroup
  } = useDeviceGroupDetails({
    groupId,
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' })
  });

  const tabsHeader = [translation.devices, translation.user_groups];

  const handleChangeTab = (_event: React.SyntheticEvent, newIndex: number) => {
    setCurrentTabIndex(newIndex);
  };

  const handleDeleteClick = () => {
    if (deviceGroup) {
      openModal(
        <DeleteDeviceGroupModal
          deviceGroupName={deviceGroup.name}
          onConfirm={async () => {
            const result = await deleteDeviceGroup();
            if (result.success) {
              closeModal();
              navigate(DEVICE_GROUPS_PATH);
            } else {
              closeModal();
              showSnackbar(result.errorMessage ?? translation.an_error_occurred, { severity: 'error' });
            }
          }}
        />
      );
    }
  };

  const handleEditClick = useCallback(() => {
    if (deviceGroup) {
      openDrawer(<EditDeviceGroupDrawer currentName={deviceGroup.name} onSave={renameDeviceGroup} />);
    } else {
      showSnackbar(translation.device_group_error_fetching_details, { severity: 'error' });
    }
  }, [openDrawer, deviceGroup, renameDeviceGroup, showSnackbar, translation]);

  const handleEditDevices = useCallback(() => {
    openDrawer(
      <EditDevicesDrawer
        currentDoorIds={doorsInGroup.map((door) => door.id)}
        allDoors={allDoors}
        onSave={updateDeviceGroupWithDoors}
      />
    );
  }, [openDrawer, doorsInGroup, allDoors, updateDeviceGroupWithDoors]);

  if (loading) {
    return (
      <Box justifyContent="center" alignContent="center" sx={{ width: '100%', height: '60vh' }}>
        <Loader />
      </Box>
    );
  }

  if (hasDeviceGroupFetchError) {
    return <SomethingWentWrong onRetry={retryFetchingDeviceGroup} />;
  }

  if (!deviceGroup) {
    return <NotFound />;
  }

  return (
    <Stack spacing={4}>
      <BreadcrumbMeta
        breadcrumb={{
          title: deviceGroup.name,
          crumbs: [
            { text: translation.devices, path: '/devices' },
            { text: translation.device_groups, path: '/devices/groups' }
          ]
        }}
      />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        useFlexGap
        spacing={1}
        sx={{ flexWrap: 'wrap' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', flexGrow: 1, flexBasis: { xs: '100%', sm: 'auto' }, minWidth: 0 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              minWidth: 0,
              '#editGroupBtn': { visibility: 'hidden', opacity: 0 },
              '&:hover #editGroupBtn': { visibility: 'visible', opacity: 1 },
              '@media (hover: none)': { '#editGroupBtn': { visibility: 'visible', opacity: 1 } }
            }}
          >
            <Typography
              variant="h4"
              color="textPrimary"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {deviceGroup.name}
            </Typography>
            <IconButton id="editGroupBtn" size="small" onClick={handleEditClick} sx={{ ml: 1, flexShrink: 0 }}>
              <EditOutlined fontSize="small" />
            </IconButton>
          </Stack>
          <SetupIndicator
            steps={[
              { label: translation.setup_add_devices, completed: doorsInGroup.length > 0 },
              { label: translation.setup_add_user_groups, completed: userGroupsForGroup.some((ug) => ug.isEnabled) }
            ]}
          />
        </Stack>
        <Button
          id="delete-device-group-button"
          onClick={handleDeleteClick}
          size="small"
          color="error"
          variant="text"
          startIcon={<DeleteOutlined color="error" />}
        >
          {translation.delete_group}
        </Button>
      </Stack>
      <Box sx={(theme) => ({ borderBottom: 1, borderColor: theme.palette.divider })}>
        <Tabs value={currentTabIndex} onChange={handleChangeTab}>
          {tabsHeader.map((header, index) => (
            <Tab key={header} id={`tab-${index}`} label={header} />
          ))}
        </Tabs>
      </Box>
      {currentTabIndex === 0 && <DevicesTab doors={doorsInGroup} onEditDevices={handleEditDevices} />}
      {currentTabIndex === 1 && <UserGroupsTab userGroups={userGroupsForGroup} onToggle={toggleUserGroupAccess} />}
    </Stack>
  );
};

export default DeviceGroupDetails;
